import { Button, CircularProgress, Stack } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

const buttonSx = {
  minHeight: 40,
  fontWeight: 700,
  borderRadius: 2,
  fontSize: "0.8125rem",
  whiteSpace: "nowrap",
};

const ActionSpinner = () => (
  <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
);

const ReviewDecisionActions = ({
  fieldDisabled,
  isSubmitting,
  loading,
  runQuickAction,
  runSaveAction,
  approveValue,
  returnValue,
  reviseValue,
  rejectValue,
  followUpValue,
  hideReferAction = false,
  hideReviseAction = false,
  layout = "row",
  showSave = true,
  showApprove = true,
  showReject = true,
  showRefer = true,
  saveLabel = "ذخیره",
  notifyApplicant = true,
}) => {
  const [pendingAction, setPendingAction] = useState(null);
  const busy = Boolean(loading || isSubmitting);
  const disabled = Boolean(fieldDisabled || busy || pendingAction);
  const isPanel = layout === "panel";

  useEffect(() => {
    if (!busy) setPendingAction(null);
  }, [busy]);

  const startAction = (actionKey, invoke) => {
    if (fieldDisabled || busy || pendingAction) return;
    setPendingAction(actionKey);
    invoke();
  };

  const showSpinner = (actionKey) => pendingAction === actionKey;

  const buttons = (
    <>
      {showSave ? (
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          disabled={disabled}
          onClick={() => startAction("save", () => runSaveAction?.())}
          sx={buttonSx}
          fullWidth={layout === "heroGrid"}
        >
          {showSpinner("save") ? <ActionSpinner /> : null}
          {pendingAction === "save" ? "در حال ذخیره..." : saveLabel}
        </Button>
      ) : null}
      {!hideReviseAction ? (
        <Button
          variant="contained"
          color="warning"
          disabled={disabled || !reviseValue}
          onClick={() =>
            startAction("revise", () =>
              runQuickAction(reviseValue, {
                bypassValidation: true,
                notifyApplicant: Boolean(notifyApplicant),
              })
            )
          }
          sx={buttonSx}
          fullWidth={layout === "heroGrid"}
        >
          {showSpinner("revise") ? <ActionSpinner /> : null}
          {pendingAction === "revise" ? "در حال ثبت..." : "نیاز به اصلاح"}
        </Button>
      ) : null}
      {followUpValue ? (
        <Button
          variant="contained"
          color="secondary"
          disabled={disabled || !followUpValue}
          onClick={() =>
            startAction("followUp", () =>
              runQuickAction(followUpValue, {
                bypassValidation: true,
                notifyApplicant: Boolean(notifyApplicant),
              })
            )
          }
          sx={buttonSx}
          fullWidth={layout === "heroGrid"}
        >
          {showSpinner("followUp") ? <ActionSpinner /> : null}
          {pendingAction === "followUp" ? "در حال ثبت..." : "نیاز به پیگیری"}
        </Button>
      ) : null}
      {showReject ? (
        <Button
          variant="contained"
          color="error"
          disabled={disabled || !rejectValue}
          onClick={() =>
            startAction("reject", () =>
              runQuickAction(rejectValue, { bypassValidation: true })
            )
          }
          sx={buttonSx}
          fullWidth={layout === "heroGrid"}
        >
          {showSpinner("reject") ? <ActionSpinner /> : null}
          {pendingAction === "reject" ? "در حال ثبت..." : "رد درخواست"}
        </Button>
      ) : null}
      {showRefer && !hideReferAction ? (
        <Button
          variant="contained"
          color="info"
          disabled={disabled}
          onClick={() =>
            startAction("refer", () => runQuickAction(returnValue))
          }
          sx={buttonSx}
          fullWidth={layout === "heroGrid"}
        >
          {showSpinner("refer") ? <ActionSpinner /> : null}
          {pendingAction === "refer" ? "در حال ثبت..." : "ارجاع به کارشناس"}
        </Button>
      ) : null}
      {showApprove ? (
        <Button
          variant="contained"
          color="success"
          disabled={disabled || !approveValue}
          onClick={() =>
            startAction("approve", () =>
              runQuickAction(approveValue, {
                notifyApplicant: Boolean(notifyApplicant),
              })
            )
          }
          sx={buttonSx}
          fullWidth={layout === "heroGrid"}
        >
          {showSpinner("approve") ? <ActionSpinner /> : null}
          {pendingAction === "approve" ? "در حال تایید..." : "تایید بررسی اولیه"}
        </Button>
      ) : null}
    </>
  );

  if (layout === "heroGrid") {
    return (
      <Stack
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(120px, 1fr))",
          gap: 1,
          width: "100%",
        }}
      >
        {buttons}
      </Stack>
    );
  }

  if (isPanel) {
    return (
      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        spacing={1}
        sx={{
          width: "100%",
          "& .MuiButton-root": {
            ...buttonSx,
            flex: "1 1 160px",
            maxWidth: { md: 240 },
          },
        }}
      >
        {buttons}
      </Stack>
    );
  }

  return (
    <Stack
      direction={layout === "stack" ? "column" : { xs: "column", md: "row" }}
      spacing={1}
      flexWrap="wrap"
      useFlexGap
      sx={{
        "& .MuiButton-root": {
          flex: layout === "stack" ? "1 1 auto" : { xs: "1 1 100%", md: "0 1 auto" },
          ...buttonSx,
        },
      }}
    >
      {buttons}
    </Stack>
  );
};

ReviewDecisionActions.propTypes = {
  fieldDisabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  loading: PropTypes.bool,
  runQuickAction: PropTypes.func.isRequired,
  runSaveAction: PropTypes.func,
  approveValue: PropTypes.string,
  returnValue: PropTypes.string,
  reviseValue: PropTypes.string,
  rejectValue: PropTypes.string,
  followUpValue: PropTypes.string,
  hideReferAction: PropTypes.bool,
  hideReviseAction: PropTypes.bool,
  layout: PropTypes.oneOf(["row", "stack", "heroGrid", "panel"]),
  showSave: PropTypes.bool,
  showApprove: PropTypes.bool,
  showReject: PropTypes.bool,
  showRefer: PropTypes.bool,
  saveLabel: PropTypes.string,
  notifyApplicant: PropTypes.bool,
};

export default ReviewDecisionActions;
